import { TuttaItalia } from '@sbt-web/networking';
import {
  CategoryID,
  GeoEntry,
  ItemCategory,
  ItemGeo,
} from '@sbt-web/network/types';
import {
  Category,
  getCategoryByID,
  getMacroOfCategoryID,
  isMacroCategory,
} from '@sbt-web/category-utils';
import type {
  InternalLink,
  ListingLinks,
  AdDetailLinks,
} from '../client/components/SEO/InternalLinks/types';
import {
  shouldMirrorRequest,
  comparePlexusResponses,
  getOptimizelyClient,
} from '@shared/plexus-client-ff-utils';
import { Client, zeroUUID } from '@sbt-web/optimizely-sdk';
import logger from '../server/logger';

interface PlexusLink {
  url: string;
  anchorText: string;
  resultType:
    | 'SEMANTIC_ALL'
    | 'SEMANTIC_L1'
    | 'SEMANTIC_L2'
    | 'RANDOM'
    | 'POPULAR';
}

/**
 * The fields we are interested in from the Plexus response
 */
interface PlexusResult {
  numberOfItems: number;
  results: PlexusLink[];
}

/**
 * The interface to call Plexus with
 */
interface PlexusRequestConfiguration {
  /**
   * The URL of the current page, used as a cache key
   */
  pageUrl: string;
  /**
   * The category of the page hosting the links:
   * * Null to represent "All Categories"
   * * With only the macro in the case of a search in a macro
   * * With both the macro and the main category in the case of a search in a
   * main category, or an ad detail.
   */
  category: { macro: string; main?: string } | null;
  /**
   * The three-level location information: region, province, and municipality.
   * Pass null for the cases where the location is not relevant.
   */
  geo: { region: string; province?: string; municipality?: string } | null;
  /**
   * The specific text to pass as the text query: the logic is specific by
   * page and category.
   */
  baseQuery: string;
  /**
   * The type of page we are requesting links for.
   */
  pageType: 'listing' | 'ad' | 'hp';
  /**
   * How many results we want to get.
   */
  numberOfResults: number;
}

const callTimeout = Number.parseInt(
  (process.env.PLEXUS_SERVER_TIMEOUT_MS as string) ??
    process.env.NEXT_PUBLIC_PLEXUS_CLIENT_TIMEOUT_MS,
  10
);

const mirrorPlexusRequest = async (
  plexusRequestUrl: URL,
  optimizelyClient: Client | null,
  getEnvId: string = zeroUUID
) => {
  const shouldMirror = await shouldMirrorRequest(optimizelyClient, getEnvId);
  const cancellationSignal = new AbortController();
  setTimeout(() => {
    cancellationSignal.abort('The request to Mirror Plexus has timed out.');
  }, callTimeout);
  if (shouldMirror && process.env.INTERNAL_LINKING_BASE_URL) {
    const mirrorURL = new URL(process.env.INTERNAL_LINKING_BASE_URL);
    plexusRequestUrl.searchParams.forEach((value, key) => {
      mirrorURL.searchParams.set(key, value);
    });
    return fetch(mirrorURL, {
      method: 'GET',
      signal: cancellationSignal.signal,
    });
  }
};

const getInternalLinks = async (
  config: PlexusRequestConfiguration,
  environmentId?: string | null
): Promise<PlexusResult> => {
  if (config.pageUrl === '') {
    throw new RangeError(
      'The page URL must be provided. It is mandatory for Plexus.'
    );
  }

  const requestURL = new URL(process.env.PLEXUS_BASE_URL as string);
  requestURL.searchParams.set('u', config.pageUrl);
  requestURL.searchParams.set('t', config.pageType);
  requestURL.searchParams.set('s', config.numberOfResults.toString());

  if (config.pageType !== 'hp') {
    requestURL.searchParams.set('q', config.baseQuery);
  }

  // Geo config
  if (config.geo !== null) {
    // Searching in all of Italy is the equivalent of not specifying a region
    requestURL.searchParams.set('r', config.geo.region);

    // Our level beneath the region is the province, called "city" by Geogos
    if (typeof config.geo.province !== 'undefined') {
      requestURL.searchParams.set('c', config.geo.province);
    }

    // Our level beneath the province is the municipality (comune), called "town" by Geogos
    if (typeof config.geo.municipality !== 'undefined') {
      requestURL.searchParams.set('d', config.geo.municipality);
    }
  }

  // Category config
  // Skip entirely if the search is in all categories
  if (config.category !== null) {
    if (typeof config.category.main !== 'undefined') {
      requestURL.searchParams.set('l1', config.category.macro);
      requestURL.searchParams.set('l2', config.category.main);
    } else {
      requestURL.searchParams.set('l1', config.category.macro);
    }
  }

  const cancellationSignal = new AbortController();
  setTimeout(() => {
    cancellationSignal.abort('The request to Plexus has timed out.');
  }, callTimeout);

  const optimizelyClient = await getOptimizelyClient();

  const plexusPromise: Promise<Response> = fetch(requestURL, {
    method: 'GET',
    signal: cancellationSignal.signal,
  });
  const mirrorPromise: Promise<Response | undefined> = mirrorPlexusRequest(
    requestURL,
    optimizelyClient,
    environmentId ?? zeroUUID
  );

  const results = await Promise.allSettled([plexusPromise, mirrorPromise]);

  const plexusResponse =
    results[0].status === 'fulfilled' ? results[0].value : undefined;
  const mirrorResponse =
    results[1].status === 'fulfilled' ? results[1].value : undefined;

  if (mirrorResponse && !mirrorResponse.ok) {
    logger.logInfo(
      `[PLEXUS] Mirror request failed: ${mirrorResponse?.status} - ${mirrorResponse?.statusText}`
    );
  }

  if (plexusResponse?.ok && mirrorResponse) {
    await comparePlexusResponses(
      plexusResponse,
      mirrorResponse,
      requestURL,
      optimizelyClient,
      environmentId ?? zeroUUID
    );
  }

  if (plexusResponse?.ok) {
    return await plexusResponse.json();
  } else if (plexusResponse) {
    throw new Error(
      `(HTTP ${plexusResponse.status}) ${await plexusResponse.text()}`
    );
  } else {
    throw new Error('Failed to fetch from Plexus service');
  }
};

/**
 * Decides if the category combination provides should include the geo information when calling Plexus.
 * It is removed for the market categories, and for the market-like categories.
 * I.E. if the macro is Motori, Immobili, or Lavoro, except Accessori Auto, Accessori Moto or Attrezzatura di Lavoro, use the geo.
 * @param macroId The ID of the macro category
 * @param mainId The ID of the main category
 */
const shouldIncludeGeo: (
  macroId: CategoryID,
  mainId?: CategoryID
) => boolean = (macroId, mainId) => {
  const eligibleMacros: CategoryID[] = [
    CategoryID.Motori,
    CategoryID.Immobili,
    CategoryID.LavoroServizi,
  ];
  const isEligibleMacro = eligibleMacros.includes(macroId);

  // If the macro is not eligible, we don't need to check the main category
  if (!isEligibleMacro) {
    return false;
  }
  // If the macro is elgible and the main is undefined, we include the geo
  else if (mainId === undefined) {
    return true;
  }
  // Otherwise check that the main category is not an exception
  else {
    const inelegibleLeafs: CategoryID[] = [
      CategoryID.AccessoriMoto,
      CategoryID.AccessoriAuto,
      CategoryID.Attrezzature,
    ];
    const isIneligibleMainInEligibleMacro = inelegibleLeafs.includes(mainId);

    return !isIneligibleMainInEligibleMacro;
  }
};

/**
 * A helper which remaps the Plexus links to the internal link structure
 * @param link The link to remap
 */
const plexusToInternalLink = (link: PlexusLink): InternalLink => {
  return { label: link.anchorText, href: link.url };
};

type PlexusDetailQueryConfiguration = Pick<
  PlexusRequestConfiguration,
  'baseQuery' | 'pageUrl'
> & {
  category: ItemCategory;
  geo: ItemGeo;
};

/**
 * Calls Plexus to get internal links for the detail page
 */
const getDetailLinks: (
  plexusConfig: PlexusDetailQueryConfiguration,
  environmentId?: string
) => Promise<AdDetailLinks> = (plexusConfig, environmentId) => {
  const includeGeo = shouldIncludeGeo(
    plexusConfig.category.parentId,
    plexusConfig.category.id
  );

  const macroName = getCategoryByID(plexusConfig.category.parentId).friendly;

  return getInternalLinks(
    {
      pageType: 'ad',
      numberOfResults: 40,
      pageUrl: plexusConfig.pageUrl,
      category: {
        macro: macroName,
        main: plexusConfig.category.friendlyName,
      },
      baseQuery: plexusConfig.baseQuery,
      geo: includeGeo
        ? {
            region: plexusConfig.geo.region.value,
            province: plexusConfig.geo.city?.value,
            municipality: plexusConfig.geo.town?.value,
          }
        : null,
    },
    environmentId
  ).then((response) => {
    const links: InternalLink[] = response.results.map(plexusToInternalLink);

    const headerLinks = links.slice(0, 10);
    const footerLinks = links.slice(10, 40);

    return { header: headerLinks, footer: footerLinks };
  });
};

type PlexusListingQueryConfiguration = Pick<
  PlexusRequestConfiguration,
  'baseQuery' | 'pageUrl'
> & {
  category: Category;
  geo: GeoEntry;
  hasFilters: boolean;
};

const getListingLinks: (
  plexusConfig: PlexusListingQueryConfiguration,
  environmentId?: string | null
) => Promise<ListingLinks> = (plexusConfig, environmentId) => {
  const possibleMacro = getMacroOfCategoryID(plexusConfig.category.id);

  const isAllCategories = plexusConfig.category.id === CategoryID.Tutte;
  const isMacro = isMacroCategory(getCategoryByID(plexusConfig.category.id));

  const categoryModel =
    // If searching in all categories, do not include any category information when querying Plexus
    isAllCategories
      ? null
      : // If searching in a macro, include only the macro category
        isMacro
        ? {
            macro: plexusConfig.category.friendly,
          }
        : {
            macro: possibleMacro.friendly,
            main: plexusConfig.category.friendly,
          };

  const isNationwideSearch = plexusConfig.geo.region.id === TuttaItalia.id;
  const includeGeo =
    // If searching in all categories, or in all of Italy, don't include the geo
    isAllCategories || isNationwideSearch
      ? false
      : // If searching in a macro, include the geo based only on it's eligibility
        isMacro
        ? shouldIncludeGeo(plexusConfig.category.id)
        : // Otherwise consider both the macro and the main category
          shouldIncludeGeo(possibleMacro.id, plexusConfig.category.id);

  // Category pages without geo location and without filters are considered homepages
  const pageType =
    plexusConfig.hasFilters || !isNationwideSearch ? 'listing' : 'hp';

  return getInternalLinks(
    {
      pageType,
      numberOfResults: 68,
      pageUrl: plexusConfig.pageUrl,
      baseQuery: plexusConfig.baseQuery,
      category: categoryModel,
      geo: includeGeo
        ? {
            region: plexusConfig.geo.region.value,
            province: plexusConfig.geo.city?.value,
            municipality: plexusConfig.geo.town?.value,
          }
        : null,
    },
    environmentId
  ).then((response) => {
    const links = response.results.map(plexusToInternalLink);

    // Correlati, Ricerche simili, Suggerimenti, Scopri anche

    const headerLinks = links.slice(0, 10);
    const sidebarLinks = links.slice(10, 38);
    const footerLinks = links.slice(38, 68);

    return {
      header: headerLinks,
      sidebar: sidebarLinks,
      footer: footerLinks,
    };
  });
};

export { getDetailLinks, getListingLinks };
export type { PlexusDetailQueryConfiguration };
